import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { masterAuth } from '../services/masterApi';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MasterProfile() {
  const [userId] = useState(() => {
    const token = localStorage.getItem('masterToken');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded.id;
      } catch {
        return null;
      }
    }
    return null;
  });

  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const { data: profileData, isLoading } = useQuery(
    ['masterProfile', userId],
    () => masterAuth.getProfile(userId),
    { enabled: !!userId, retry: 1 }
  );

  useEffect(() => {
    if (profileData?.data?.profile) {
      setFormData(profileData.data.profile);
    }
  }, [profileData]);

  const updateMutation = useMutation(
    (data) => masterAuth.updateProfile(userId, data),
    {
      onSuccess: () => {
        toast.success('Profile updated successfully!');
        setEditMode(false);
      },
      onError: (error) => {
        toast.error(error.error || 'Failed to update profile');
      },
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleCancel = () => {
    setFormData(profileData?.data?.profile);
    setEditMode(false);
  };

  if (!userId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Unable to load profile. Please login again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and information</p>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="bg-white rounded-t-lg shadow-md border-b border-gray-200">
              <div className="flex">
                <TabButton
                  label="Profile Information"
                  isActive={activeTab === 'profile'}
                  onClick={() => setActiveTab('profile')}
                />
                <TabButton
                  label="Change Password"
                  isActive={activeTab === 'password'}
                  onClick={() => setActiveTab('password')}
                />
                <TabButton
                  label="Preferences"
                  isActive={activeTab === 'preferences'}
                  onClick={() => setActiveTab('preferences')}
                />
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-b-lg shadow-md p-6">
              {activeTab === 'profile' && (
                <ProfileTab
                  formData={formData}
                  editMode={editMode}
                  loading={updateMutation.isLoading}
                  onEdit={() => setEditMode(true)}
                  onChange={handleInputChange}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              )}

              {activeTab === 'password' && (
                <PasswordTab userId={userId} />
              )}

              {activeTab === 'preferences' && (
                <PreferencesTab userId={userId} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TabButton({ label, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 font-semibold transition-colors ${
        isActive
          ? 'border-b-2 border-blue-600 text-blue-600'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {label}
    </button>
  );
}

function ProfileTab({ formData, editMode, loading, onEdit, onChange, onSave, onCancel }) {
  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {formData.name?.charAt(0).toUpperCase() || 'M'}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{formData.name}</h2>
            <p className="text-gray-600">{formData.businessName}</p>
          </div>
        </div>
        {!editMode && (
          <button
            onClick={onEdit}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
          >
            Edit Profile
          </button>
        )}
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Full Name"
          name="name"
          value={formData.name || ''}
          onChange={onChange}
          disabled={!editMode}
        />
        <FormField
          label="Email"
          name="email"
          type="email"
          value={formData.email || ''}
          onChange={onChange}
          disabled={true}
        />
        <FormField
          label="Business Name"
          name="businessName"
          value={formData.businessName || ''}
          onChange={onChange}
          disabled={!editMode}
        />
        <FormField
          label="NTN/CNIC"
          name="ntnCnic"
          value={formData.ntnCnic || ''}
          onChange={onChange}
          disabled={true}
        />
        <FormField
          label="Contact Number"
          name="contactNumber"
          type="tel"
          value={formData.contactNumber || ''}
          onChange={onChange}
          disabled={!editMode}
        />
        <FormField
          label="Address"
          name="address"
          value={formData.address || ''}
          onChange={onChange}
          disabled={!editMode}
        />
      </div>

      {/* Account Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatusItem label="Account Status" value={formData.status || 'active'} />
          <StatusItem label="Member Since" value={new Date(formData.createdDate).toLocaleDateString()} />
          <StatusItem label="Last Updated" value={new Date(formData.updatedDate).toLocaleDateString()} />
          <StatusItem label="Account Role" value="Master User" />
        </div>
      </div>

      {/* Action Buttons */}
      {editMode && (
        <div className="flex gap-3 justify-end pt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-6 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded transition-colors"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

function PasswordTab({ userId }) {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await masterAuth.changePassword(userId, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });

      if (result.success) {
        toast.success('Password changed successfully!');
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-md space-y-6">
      <FormField
        label="Current Password"
        name="currentPassword"
        type="password"
        value={passwords.currentPassword}
        onChange={handleChange}
        required
      />
      <FormField
        label="New Password"
        name="newPassword"
        type="password"
        value={passwords.newPassword}
        onChange={handleChange}
        required
        hint="Minimum 8 characters"
      />
      <FormField
        label="Confirm Password"
        name="confirmPassword"
        type="password"
        value={passwords.confirmPassword}
        onChange={handleChange}
        required
      />

      <button
        type="submit"
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded transition-colors"
      >
        {loading ? 'Updating...' : 'Change Password'}
      </button>
    </form>
  );
}

function PreferencesTab({ userId }) {
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    invoiceReminders: true,
    weeklyReport: false,
  });

  const handleToggle = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-4 max-w-md">
      <PreferenceItem
        label="Email Notifications"
        description="Receive email notifications for important updates"
        value={preferences.emailNotifications}
        onChange={() => handleToggle('emailNotifications')}
      />
      <PreferenceItem
        label="Invoice Reminders"
        description="Get reminders for pending invoices"
        value={preferences.invoiceReminders}
        onChange={() => handleToggle('invoiceReminders')}
      />
      <PreferenceItem
        label="Weekly Report"
        description="Receive a summary of your invoicing activity every week"
        value={preferences.weeklyReport}
        onChange={() => handleToggle('weeklyReport')}
      />

      <button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded transition-colors">
        Save Preferences
      </button>
    </div>
  );
}

function FormField({ label, name, type = 'text', value, onChange, disabled, required, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function StatusItem({ label, value }) {
  return (
    <div>
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function PreferenceItem({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div>
        <p className="font-semibold text-gray-900">{label}</p>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={value}
          onChange={onChange}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );
}
